import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import "./AdminDashboardStyles.css";
import { DjDevice, DjStats, DjUser, FeeTier, Order, PayoutEvent } from "../components/admin-dj/types";
import { DjSummaryRow } from "../components/admin-dj/Summary";
import { DjPayoutHistory } from "../components/admin-dj/PayoutHistory";
import { DjTopFans } from "../components/admin-dj/TopFans";
import { DjDisputes } from "../components/admin-dj/Disputes";
import { DjUnpaidOrders } from "../components/admin-dj/UnpaidOrders";
import AdminLoadFailed from "../components/AdminLoadFailed";
import { isNotFound, withQuietRetry } from "../utils/adminRetry";

export default function AdminDjProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [dj, setDj] = useState<DjUser | null>(null);
  const [stats, setStats] = useState<DjStats | null>(null);
  const [feeTier, setFeeTier] = useState<FeeTier | null>(null);
  const [devices, setDevices] = useState<DjDevice[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payouts, setPayouts] = useState<PayoutEvent[]>([]);
  const [payoutsFailed, setPayoutsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  // "notFound" only when the server says this talent isn't there; any other
  // failure is "Couldn't load" + Retry, never "Talent Not Found".
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDeleteOrder = async () => {
    if (!deleteId) return;
    try {
      await adminApi.delete(`/orders/${deleteId}`);
      setOrders((prev) => prev.filter((o) => o._id !== deleteId));
      setDeleteId(null);
    } catch (err: any) {
      // The server keeps any request that carried money (paid, held, played,
      // refunded or disputed) and says why.
      alert(err.response?.data?.msg || "Failed to delete order");
    }
  };

  // The Payout History card's own read: a failure shows on the card
  // ("Couldn't load payout history" + Retry), never as "no payouts".
  const loadPayouts = useCallback(async () => {
    if (!id) return;
    setPayoutsFailed(false);
    try {
      const { data } = await withQuietRetry(() => adminApi.get(`/dj/${id}/payouts`));
      setPayouts(data);
    } catch {
      setPayoutsFailed(true);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    const profile = withQuietRetry(() => adminApi.get(`/dj/${id}`));
    const payoutsDone = loadPayouts();
    try {
      const djRes = await profile;
      setDj(djRes.data.user);
      setStats(djRes.data.stats);
      setFeeTier(djRes.data.feeTier || null);
      setDevices(djRes.data.devices || []);
      setOrders(djRes.data.recentOrders);
    } catch (err) {
      // 400 = not a talent id at all (the server's "Invalid ID"), as on the
      // review page: no Retry can find it.
      const status = (err as { response?: { status?: number } })?.response?.status;
      setLoadError(isNotFound(err) || status === 400 ? "notFound" : "failed");
    }
    // The page appears once both reads are back, as it always did.
    await payoutsDone;
    setLoading(false);
  }, [id, loadPayouts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div id="adm-root-light" style={{ alignItems: "center", justifyContent: "center" }}>
        <p className="muted-text">Loading...</p>
      </div>
    );
  }

  if (!dj && loadError !== "notFound") {
    return (
      <div id="adm-root-light" style={{ alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <AdminLoadFailed what="this talent" onRetry={fetchData} />
        <button className="adm-btn-light" onClick={() => navigate("/admin/dashboard")}>← Back</button>
      </div>
    );
  }

  if (!dj) {
    return (
      <div id="adm-root-light" style={{ alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <h3>Talent Not Found</h3>
        <button className="adm-btn-light" onClick={() => navigate("/admin/dashboard")}>← Back</button>
      </div>
    );
  }

  return (
    <div id="adm-root-light">
      <main id="adm-main-light">
        {/* Header */}
        <header id="adm-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="adm-btn-light" onClick={() => navigate("/admin/dashboard")}>
              ← Back
            </button>
            <div>
              <h1 className="adm-heading" style={{ fontSize: "1.25rem" }}>
                {dj.name}
              </h1>
              {dj.isFrozen && (
                <span className="badge badge-sm badge-frozen" style={{ marginTop: 2 }}>
                  {dj.freezeType === "permanent" ? "Permanently Frozen" : "Temporarily Frozen"}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="adm-content-area">
          {/* Profile + Stats row */}
          <DjSummaryRow dj={dj} setDj={setDj} feeTier={feeTier} devices={devices} stats={stats} orders={orders} />

          {/* Payout History */}
          <DjPayoutHistory payouts={payouts} failed={payoutsFailed} onRetry={loadPayouts} />

          {/* Top Fans */}
          <DjTopFans orders={orders} />

          {/* Disputes */}
          <DjDisputes id={id} />

          {/* Selected (unpaid) */}
          <DjUnpaidOrders orders={orders} setDeleteId={setDeleteId} />
        </div>

        {/* Delete confirmation modal */}
        {deleteId && (
          <div className="adm-modal-backdrop" onClick={() => setDeleteId(null)}>
            <div className="adm-modal-box" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: "0 0 8px" }}>Delete Order</h3>
              <p className="adm-fs-88" style={{ color: "#6b7280", margin: "0 0 16px" }}>
                Are you sure you want to permanently delete this order? This action cannot be undone.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="adm-btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                <button className="adm-btn-sm adm-btn-red-sm" onClick={handleDeleteOrder}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
